import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { EMAIL_TEMPLATE_NAMES } from '../dto/system-email-templates.dto.js';
import { normalizeTemplateHtmlForSave } from '../model/system-email-templates.model.js';

function SystemEmailTemplatesListPageView({
  rows,
  jurisdictions,
  selectedJurisdictionId,
  isJurisdictionsLoading,
  isLoading,
  isSaving,
  error,
  success,
  languageOptions,
  placeholderOptions,
  wrapLink,
  detailsRow,
  editingTargetId,
  isCreating,
  form,
  formErrors,
  setForm,
  setFormErrors,
  setSelectedJurisdictionId,
  updateJurisdictionQuery,
  openCreate,
  openDetails,
  openEdit,
  closeModals,
  saveEdit,
}) {
  const { t } = useTranslation();
  const htmlTextareaRef = useRef(null);
  const quillRef = useRef(null);
  const [htmlMode, setHtmlMode] = useState('editor');

  useEffect(() => {
    if (editingTargetId || isCreating) {
      setHtmlMode('editor');
    }
  }, [editingTargetId, isCreating]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block', 'link'],
      ['clean'],
    ],
  };

  const insertPlaceholderAtCursor = (placeholder) => {
    const value = String(placeholder || '').trim();
    if (!value) {
      return;
    }

    if (htmlMode === 'editor') {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        return;
      }

      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      const length = range ? range.length : 0;

      if (length > 0) {
        quill.deleteText(index, length, 'user');
      }
      quill.insertText(index, value, 'user');
      quill.setSelection(index + value.length, 0, 'silent');
      quill.focus();

      setForm((prev) => ({ ...prev, html: quill.root?.innerHTML || '' }));
      setFormErrors((prev) => {
        if (!prev.html) {
          return prev;
        }

        const next = { ...prev };
        delete next.html;
        return next;
      });
      return;
    }

    const textarea = htmlTextareaRef.current;
    const source = String(form.html || '');
    const start = textarea && Number.isFinite(textarea.selectionStart) ? textarea.selectionStart : source.length;
    const end = textarea && Number.isFinite(textarea.selectionEnd) ? textarea.selectionEnd : source.length;
    const nextHtml = `${source.slice(0, start)}${value}${source.slice(end)}`;
    const nextCursorPosition = start + value.length;

    setForm((prev) => ({ ...prev, html: nextHtml }));
    setFormErrors((prev) => {
      if (!prev.html) {
        return prev;
      }

      const next = { ...prev };
      delete next.html;
      return next;
    });

    if (textarea) {
      window.requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });
    }
  };

  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('emailTemplates.savingInfo') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('emailTemplates.title')}</h2>
          <p>{t('emailTemplates.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link className="btn btn-outline-secondary" to={wrapLink}>
            {t('emailTemplates.headerFooter')}
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
            disabled={isLoading || isSaving || !selectedJurisdictionId}
          >
            {t('emailTemplates.addTemplate')}
          </button>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <div style={{ minWidth: '280px', maxWidth: '420px', width: '100%' }}>
            <label className="form-label mb-1">{t('emailTemplates.jurisdictionLabel')}</label>
            <select
              className="form-select"
              value={selectedJurisdictionId}
              onChange={(event) => {
                const nextId = String(event.target.value || '').trim();
                setSelectedJurisdictionId(nextId);
                updateJurisdictionQuery(nextId);
              }}
              disabled={isJurisdictionsLoading || isSaving}
            >
              {jurisdictions.length === 0 ? <option value="">{t('emailTemplates.noJurisdictions')}</option> : null}
              {jurisdictions.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="card card-body mt-3">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th>{t('emailTemplates.colName')}</th>
                    <th>{t('emailTemplates.colSubject')}</th>
                    <th>{t('emailTemplates.colLanguage')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('emailTemplates.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">{t('emailTemplates.noTemplatesFound')}</td>
                    </tr>
                  ) : null}
                  {rows.map((row) => (
                    <tr key={row.key} className="app-clickable-row" onClick={() => openDetails(row.id)}>
                      <td>{row.name}</td>
                      <td>{row.subject || '-'}</td>
                      <td>{row.language || '-'}</td>
                      <td className="text-nowrap text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            title="Edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row);
                            }}
                            disabled={isSaving}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </LoadingContainer>
        </div>
      </div>

      {detailsRow ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('emailTemplates.templateDetails')}</h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3"><strong>{t('emailTemplates.fieldId')}:</strong><div>{detailsRow.id || '-'}</div></div>
                  <div className="mb-3"><strong>{t('emailTemplates.fieldName')}:</strong><div>{detailsRow.name}</div></div>
                  <div className="mb-3"><strong>{t('emailTemplates.fieldLanguage')}:</strong><div>{detailsRow.language || '-'}</div></div>
                  <div className="mb-3"><strong>{t('emailTemplates.fieldSubject')}:</strong><div>{detailsRow.subject || '-'}</div></div>
                  <div><strong>{t('emailTemplates.fieldHtml')}:</strong><pre className="app-json mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{detailsRow.html || '-'}</pre></div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {editingTargetId || isCreating ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isCreating ? 'Add Template' : `${form.name || 'Template'}${form.language ? ` (${form.language})` : ''}`}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModals}></button>
                </div>
                <form onSubmit={saveEdit}>
                  <div className="modal-body">
                    {isCreating ? (
                      <div className="row g-3 mb-3">
                        <div className="col">
                          <label className="form-label">{t('emailTemplates.nameLabel')}</label>
                          <select
                            className={`form-select ${formErrors.name ? 'is-invalid' : ''}`}
                            value={form.name}
                            onChange={(event) => {
                              const value = event.target.value;
                              setForm((prev) => ({ ...prev, name: value }));
                              setFormErrors((prev) => {
                                if (!prev.name) {
                                  return prev;
                                }
                                const next = { ...prev };
                                delete next.name;
                                return next;
                              });
                            }}
                            required
                          >
                            {EMAIL_TEMPLATE_NAMES.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          {formErrors.name ? <div className="invalid-feedback">{formErrors.name}</div> : null}
                        </div>
                        <div className="col-auto" style={{ minWidth: '120px' }}>
                          <label className="form-label">{t('emailTemplates.languageLabel')}</label>
                          <select
                            className={`form-select ${formErrors.language ? 'is-invalid' : ''}`}
                            value={form.language}
                            onChange={(event) => {
                              const value = event.target.value;
                              setForm((prev) => ({ ...prev, language: value }));
                              setFormErrors((prev) => {
                                if (!prev.language) {
                                  return prev;
                                }
                                const next = { ...prev };
                                delete next.language;
                                return next;
                              });
                            }}
                            required
                          >
                            {languageOptions.map((code) => (
                              <option key={code} value={code}>{code}</option>
                            ))}
                          </select>
                          {formErrors.language ? <div className="invalid-feedback">{formErrors.language}</div> : null}
                        </div>
                      </div>
                    ) : null}
                    <div className="row g-3 mb-3 align-items-end">
                      <div className="col">
                        <label className="form-label">{t('emailTemplates.subjectLabel')}</label>
                        <input
                          className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                          value={form.subject}
                          onChange={(event) => {
                            const value = event.target.value;
                            setForm((prev) => ({ ...prev, subject: value }));
                            setFormErrors((prev) => {
                              if (!prev.subject) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.subject;
                              return next;
                            });
                          }}
                          required
                        />
                        {formErrors.subject ? <div className="invalid-feedback">{formErrors.subject}</div> : null}
                      </div>
                    </div>
                    <div>
                      {placeholderOptions.length > 0 ? (
                        <div className="mb-2">
                          <label className="form-label">{t('emailTemplates.placeholdersLabel')}</label>
                          <div className="d-flex flex-wrap gap-2">
                            {placeholderOptions.map((placeholder) => (
                              <button
                                key={placeholder}
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => insertPlaceholderAtCursor(placeholder)}
                                disabled={isSaving}
                              >
                                {placeholder}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label mb-0">{t('emailTemplates.htmlLabel')}</label>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`small fw-semibold ${htmlMode === 'editor' ? 'text-primary' : 'text-muted'}`}>{t('emailTemplates.editorMode')}</span>
                          <div className="form-check form-switch m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={htmlMode === 'html'}
                              onChange={(event) => {
                                const nextMode = event.target.checked ? 'html' : 'editor';
                                if (nextMode === 'html') {
                                  const normalizedHtml = normalizeTemplateHtmlForSave(form.html);
                                  if (normalizedHtml !== form.html) {
                                    setForm((prev) => ({ ...prev, html: normalizedHtml }));
                                  }
                                }
                                setHtmlMode(nextMode);
                              }}
                              disabled={isSaving}
                            />
                          </div>
                          <span className={`small fw-semibold ${htmlMode === 'html' ? 'text-primary' : 'text-muted'}`}>{t('emailTemplates.htmlMode')}</span>
                        </div>
                      </div>
                      {htmlMode === 'editor' ? (
                        <ReactQuill
                          ref={quillRef}
                          theme="snow"
                          className="app-email-template-editor"
                          value={form.html}
                          modules={quillModules}
                          onChange={(value) => {
                            setForm((prev) => ({ ...prev, html: value }));
                            setFormErrors((prev) => {
                              if (!prev.html) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.html;
                              return next;
                            });
                          }}
                          readOnly={isSaving}
                        />
                      ) : (
                        <textarea
                          ref={htmlTextareaRef}
                          rows="12"
                          className={`form-control font-monospace ${formErrors.html ? 'is-invalid' : ''}`}
                          value={form.html}
                          onChange={(event) => {
                            const value = event.target.value;
                            setForm((prev) => ({ ...prev, html: value }));
                            setFormErrors((prev) => {
                              if (!prev.html) {
                                return prev;
                              }
                              const next = { ...prev };
                              delete next.html;
                              return next;
                            });
                          }}
                          required
                        />
                      )}
                      {formErrors.html ? <div className="invalid-feedback">{formErrors.html}</div> : null}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModals} disabled={isSaving}>{t('common.cancel')}</button>
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>{t('common.save')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </div>
  );
}

export default SystemEmailTemplatesListPageView;
